// src/components/CourseUpload.js
import React, { useState } from "react";
import {
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase-config";
import ScorecardInputTable from "./ScorecardInputTable";

// Define default course-level fields.
const initialCourseData = {
  club_name: "",
  course_name: "",
  location_address: "",
  location_city: "",
  location_state: "",
  location_country: "",
  location_latitude: "",
  location_longitude: "",
  number_of_holes: "",
};

// Default tee-level fields. Each tee has its own holes array.
const initialTeeData = {
  tee_name: "",
  course_rating: "",
  slope_rating: "",
  bogey_rating: "",
  total_yards: "",
  total_meters: "",
  par_total: "",
  front_course_rating: "",
  front_slope_rating: "",
  front_bogey_rating: "",
  back_course_rating: "",
  back_slope_rating: "",
  back_bogey_rating: "",
  holes: [], // holes will be objects of shape: { yardage: "", par: "", handicap: "" }
};

const CourseUpload = () => {
  // Course-level state.
  const [courseData, setCourseData] = useState(initialCourseData);
  // Separate state arrays for male and female tees.
  const [maleTees, setMaleTees] = useState([]);
  const [femaleTees, setFemaleTees] = useState([]);
  // Status message state.
  const [courseStatusMsg, setCourseStatusMsg] = useState("");

  // Update course-level text fields.
  const handleCourseInputChange = (e) => {
    const { name, value } = e.target;
    setCourseData((prev) => ({ ...prev, [name]: value }));
  };

  // Update the global number of holes via dropdown.
  const handleHoleCountChange = (e) => {
    const count = parseInt(e.target.value, 10);
    setCourseData((prev) => ({ ...prev, number_of_holes: count }));
  };

  // ------------------ Male Tee Handlers ------------------
  const addMaleTee = () => setMaleTees((prev) => [...prev, { ...initialTeeData }]);
  const removeMaleTee = (index) => setMaleTees((prev) => prev.filter((_, i) => i !== index));
  const handleMaleTeeChange = (index, e) => {
    const { name, value } = e.target;
    const updated = [...maleTees];
    updated[index] = { ...updated[index], [name]: value };
    setMaleTees(updated);
  };

  // Initialize holes for a male tee if needed, then update the specific hole field.
  const handleMaleTeeHoleChange = (teeIndex, holeIndex, field, value) => {
    const updated = [...maleTees];
    if (!updated[teeIndex].holes.length && courseData.number_of_holes) {
      updated[teeIndex].holes = Array.from(
        { length: courseData.number_of_holes },
        () => ({ yardage: "", par: "", handicap: "" })
      );
    }
    updated[teeIndex].holes[holeIndex] = {
      ...updated[teeIndex].holes[holeIndex],
      [field]: value,
    };
    setMaleTees(updated);
  };

  // ------------------ Female Tee Handlers ------------------
  const addFemaleTee = () => setFemaleTees((prev) => [...prev, { ...initialTeeData }]);
  const removeFemaleTee = (index) => setFemaleTees((prev) => prev.filter((_, i) => i !== index));
  const handleFemaleTeeChange = (index, e) => {
    const { name, value } = e.target;
    const updated = [...femaleTees];
    updated[index] = { ...updated[index], [name]: value };
    setFemaleTees(updated);
  };

  const handleFemaleTeeHoleChange = (teeIndex, holeIndex, field, value) => {
    const updated = [...femaleTees];
    if (!updated[teeIndex].holes.length && courseData.number_of_holes) {
      updated[teeIndex].holes = Array.from(
        { length: courseData.number_of_holes },
        () => ({ yardage: "", par: "", handicap: "" })
      );
    }
    updated[teeIndex].holes[holeIndex] = {
      ...updated[teeIndex].holes[holeIndex],
      [field]: value,
    };
    setFemaleTees(updated);
  };

  // --------- Submit Course and Ensure Numeric Conversions ---------
  const handleCourseSubmit = async (e) => {
    e.preventDefault();
    setCourseStatusMsg("");

    // Build the new course object with proper numeric conversion.
    const newCourse = {
      club_name: courseData.club_name,
      course_name: courseData.course_name,
      location: {
        address: courseData.location_address,
        city: courseData.location_city,
        state: courseData.location_state,
        country: courseData.location_country,
        latitude: parseFloat(courseData.location_latitude), // numeric
        longitude: parseFloat(courseData.location_longitude), // numeric
      },
      number_of_holes: courseData.number_of_holes
        ? parseInt(courseData.number_of_holes, 10)
        : 18,
      tees: {
        male: maleTees.map((tee) => ({
          tee_name: tee.tee_name,
          course_rating: parseFloat(tee.course_rating), // convert to number
          slope_rating: parseFloat(tee.slope_rating),
          bogey_rating: parseFloat(tee.bogey_rating),
          total_yards: parseInt(tee.total_yards, 10),
          total_meters: parseInt(tee.total_meters, 10),
          number_of_holes: parseInt(courseData.number_of_holes, 10),
          par_total: parseInt(tee.par_total, 10),
          front_course_rating: parseFloat(tee.front_course_rating),
          front_slope_rating: parseFloat(tee.front_slope_rating),
          front_bogey_rating: parseFloat(tee.front_bogey_rating),
          back_course_rating: parseFloat(tee.back_course_rating),
          back_slope_rating: parseFloat(tee.back_slope_rating),
          back_bogey_rating: parseFloat(tee.back_bogey_rating),
          // Convert each hole field to numbers.
          holes: (tee.holes || []).map((h) => ({
            yardage: parseInt(h.yardage, 10),
            par: parseInt(h.par, 10),       // now including par as number
            handicap: parseInt(h.handicap, 10),
          })),
        })),
        female: femaleTees.map((tee) => ({
          tee_name: tee.tee_name,
          course_rating: parseFloat(tee.course_rating),
          slope_rating: parseFloat(tee.slope_rating),
          bogey_rating: parseFloat(tee.bogey_rating),
          total_yards: parseInt(tee.total_yards, 10),
          total_meters: parseInt(tee.total_meters, 10),
          number_of_holes: parseInt(courseData.number_of_holes, 10),
          par_total: parseInt(tee.par_total, 10),
          front_course_rating: parseFloat(tee.front_course_rating),
          front_slope_rating: parseFloat(tee.front_slope_rating),
          front_bogey_rating: parseFloat(tee.front_bogey_rating),
          back_course_rating: parseFloat(tee.back_course_rating),
          back_slope_rating: parseFloat(tee.back_slope_rating),
          back_bogey_rating: parseFloat(tee.back_bogey_rating),
          holes: (tee.holes || []).map((h) => ({
            yardage: parseInt(h.yardage, 10),
            par: parseInt(h.par, 10),       // include par conversion here
            handicap: parseInt(h.handicap, 10),
          })),
        })),
      },
      timestamp: serverTimestamp(),
    };

    try {
      await addDoc(collection(db, "courses"), newCourse);
      setCourseStatusMsg("Course submitted successfully.");
      // Reset state.
      setCourseData(initialCourseData);
      setMaleTees([]);
      setFemaleTees([]);
    } catch (error) {
      console.error("Error adding course:", error);
      setCourseStatusMsg("Failed to add course.");
    }
  };

  return (
    <Paper elevation={2} sx={{ p: 2, my: 2 }}>
      <Typography variant="h5" gutterBottom>
        Add New Course
      </Typography>
      {courseStatusMsg && (
        <Typography variant="body1" color="primary" sx={{ mb: 2 }}>
          {courseStatusMsg}
        </Typography>
      )}
      <Box
        component="form"
        onSubmit={handleCourseSubmit}
        sx={{ display: "flex", flexDirection: "column", gap: 2 }}
      >
        {/* Course-Level Inputs */}
        <TextField
          label="Club Name"
          name="club_name"
          value={courseData.club_name}
          onChange={handleCourseInputChange}
          fullWidth
        />
        <TextField
          label="Course Name"
          name="course_name"
          value={courseData.course_name}
          onChange={handleCourseInputChange}
          fullWidth
        />
        <TextField
          label="Address"
          name="location_address"
          value={courseData.location_address}
          onChange={handleCourseInputChange}
          fullWidth
        />
        <TextField
          label="City"
          name="location_city"
          value={courseData.location_city}
          onChange={handleCourseInputChange}
          fullWidth
        />
        <TextField
          label="State"
          name="location_state"
          value={courseData.location_state}
          onChange={handleCourseInputChange}
          fullWidth
        />
        <TextField
          label="Country"
          name="location_country"
          value={courseData.location_country}
          onChange={handleCourseInputChange}
          fullWidth
        />
        <TextField
          label="Latitude"
          name="location_latitude"
          value={courseData.location_latitude}
          onChange={handleCourseInputChange}
          fullWidth
        />
        <TextField
          label="Longitude"
          name="location_longitude"
          value={courseData.location_longitude}
          onChange={handleCourseInputChange}
          fullWidth
        />
        <FormControl fullWidth>
          <InputLabel id="hole-count-label">Number of Holes</InputLabel>
          <Select
            labelId="hole-count-label"
            label="Number of Holes"
            name="number_of_holes"
            value={courseData.number_of_holes || ""}
            onChange={handleHoleCountChange}
          >
            <MenuItem value={9}>9 Holes</MenuItem>
            <MenuItem value={18}>18 Holes</MenuItem>
          </Select>
        </FormControl>

        {/* Male Tees */}
        <Typography variant="subtitle1">Male Tees</Typography>
        {maleTees.map((tee, index) => (
          <Box
            key={index}
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              border: "1px solid #ddd",
              p: 1,
              borderRadius: 1,
            }}
          >
            <Typography variant="body2">Tee #{index + 1}</Typography>
            <TextField
              label="Tee Name"
              name="tee_name"
              value={tee.tee_name}
              onChange={(e) => handleMaleTeeChange(index, e)}
              fullWidth
            />
            <TextField
              label="Course Rating"
              name="course_rating"
              value={tee.course_rating}
              onChange={(e) => handleMaleTeeChange(index, e)}
              fullWidth
            />
            <TextField
              label="Slope Rating"
              name="slope_rating"
              value={tee.slope_rating}
              onChange={(e) => handleMaleTeeChange(index, e)}
              fullWidth
            />
            <TextField
              label="Bogey Rating"
              name="bogey_rating"
              value={tee.bogey_rating}
              onChange={(e) => handleMaleTeeChange(index, e)}
              fullWidth
            />
            <TextField
              label="Total Yards"
              name="total_yards"
              value={tee.total_yards}
              onChange={(e) => handleMaleTeeChange(index, e)}
              fullWidth
            />
            <TextField
              label="Total Meters"
              name="total_meters"
              value={tee.total_meters}
              onChange={(e) => handleMaleTeeChange(index, e)}
              fullWidth
            />
            <TextField
              label="Par Total"
              name="par_total"
              value={tee.par_total}
              onChange={(e) => handleMaleTeeChange(index, e)}
              fullWidth
            />
            <TextField
              label="Front Course Rating"
              name="front_course_rating"
              value={tee.front_course_rating}
              onChange={(e) => handleMaleTeeChange(index, e)}
              fullWidth
            />
            <TextField
              label="Front Slope Rating"
              name="front_slope_rating"
              value={tee.front_slope_rating}
              onChange={(e) => handleMaleTeeChange(index, e)}
              fullWidth
            />
            <TextField
              label="Front Bogey Rating"
              name="front_bogey_rating"
              value={tee.front_bogey_rating}
              onChange={(e) => handleMaleTeeChange(index, e)}
              fullWidth
            />
            <TextField
              label="Back Course Rating"
              name="back_course_rating"
              value={tee.back_course_rating}
              onChange={(e) => handleMaleTeeChange(index, e)}
              fullWidth
            />
            <TextField
              label="Back Slope Rating"
              name="back_slope_rating"
              value={tee.back_slope_rating}
              onChange={(e) => handleMaleTeeChange(index, e)}
              fullWidth
            />
            <TextField
              label="Back Bogey Rating"
              name="back_bogey_rating"
              value={tee.back_bogey_rating}
              onChange={(e) => handleMaleTeeChange(index, e)}
              fullWidth
            />
            {tee.tee_name && tee.course_rating && tee.slope_rating && courseData.number_of_holes && (
              <ScorecardInputTable
                holes={
                  tee.holes && tee.holes.length
                    ? tee.holes
                    : Array.from({ length: courseData.number_of_holes }, () => ({
                        yardage: "",
                        par: "",
                        handicap: "",
                      }))
                }
                handleHoleChange={(holeIndex, field, value) =>
                  handleMaleTeeHoleChange(index, holeIndex, field, value)
                }
                holeCount={parseInt(courseData.number_of_holes, 10)}
                holeOffset={0}
              />
            )}
            <Button variant="outlined" onClick={() => removeMaleTee(index)}>
              Remove Male Tee
            </Button>
          </Box>
        ))}
        <Button variant="outlined" onClick={addMaleTee}>
          Add Male Tee
        </Button>

        {/* Female Tees */}
        <Typography variant="subtitle1">Female Tees</Typography>
        {femaleTees.map((tee, index) => (
          <Box
            key={index}
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              border: "1px solid #ddd",
              p: 1,
              borderRadius: 1,
            }}
          >
            <Typography variant="body2">Tee #{index + 1}</Typography>
            <TextField
              label="Tee Name"
              name="tee_name"
              value={tee.tee_name}
              onChange={(e) => handleFemaleTeeChange(index, e)}
              fullWidth
            />
            <TextField
              label="Course Rating"
              name="course_rating"
              value={tee.course_rating}
              onChange={(e) => handleFemaleTeeChange(index, e)}
              fullWidth
            />
            <TextField
              label="Slope Rating"
              name="slope_rating"
              value={tee.slope_rating}
              onChange={(e) => handleFemaleTeeChange(index, e)}
              fullWidth
            />
            <TextField
              label="Bogey Rating"
              name="bogey_rating"
              value={tee.bogey_rating}
              onChange={(e) => handleFemaleTeeChange(index, e)}
              fullWidth
            />
            <TextField
              label="Total Yards"
              name="total_yards"
              value={tee.total_yards}
              onChange={(e) => handleFemaleTeeChange(index, e)}
              fullWidth
            />
            <TextField
              label="Total Meters"
              name="total_meters"
              value={tee.total_meters}
              onChange={(e) => handleFemaleTeeChange(index, e)}
              fullWidth
            />
            <TextField
              label="Par Total"
              name="par_total"
              value={tee.par_total}
              onChange={(e) => handleFemaleTeeChange(index, e)}
              fullWidth
            />
            <TextField
              label="Front Course Rating"
              name="front_course_rating"
              value={tee.front_course_rating}
              onChange={(e) => handleFemaleTeeChange(index, e)}
              fullWidth
            />
            <TextField
              label="Front Slope Rating"
              name="front_slope_rating"
              value={tee.front_slope_rating}
              onChange={(e) => handleFemaleTeeChange(index, e)}
              fullWidth
            />
            <TextField
              label="Front Bogey Rating"
              name="front_bogey_rating"
              value={tee.front_bogey_rating}
              onChange={(e) => handleFemaleTeeChange(index, e)}
              fullWidth
            />
            <TextField
              label="Back Course Rating"
              name="back_course_rating"
              value={tee.back_course_rating}
              onChange={(e) => handleFemaleTeeChange(index, e)}
              fullWidth
            />
            <TextField
              label="Back Slope Rating"
              name="back_slope_rating"
              value={tee.back_slope_rating}
              onChange={(e) => handleFemaleTeeChange(index, e)}
              fullWidth
            />
            <TextField
              label="Back Bogey Rating"
              name="back_bogey_rating"
              value={tee.back_bogey_rating}
              onChange={(e) => handleFemaleTeeChange(index, e)}
              fullWidth
            />
            {tee.tee_name && tee.course_rating && tee.slope_rating && courseData.number_of_holes && (
              <ScorecardInputTable
                holes={
                  tee.holes && tee.holes.length
                    ? tee.holes
                    : Array.from({ length: courseData.number_of_holes }, () => ({
                        yardage: "",
                        par: "",
                        handicap: "",
                      }))
                }
                handleHoleChange={(holeIndex, field, value) =>
                  handleFemaleTeeHoleChange(index, holeIndex, field, value)
                }
                holeCount={parseInt(courseData.number_of_holes, 10)}
                holeOffset={0}
              />
            )}
            <Button variant="outlined" onClick={() => removeFemaleTee(index)}>
              Remove Female Tee
            </Button>
          </Box>
        ))}
        <Button variant="outlined" onClick={addFemaleTee}>
          Add Female Tee
        </Button>
        <Button type="submit" variant="contained" color="primary">
          Add Course
        </Button>
      </Box>
    </Paper>
  );
};

export default CourseUpload;